import { styled } from '@linaria/react';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import { setDateRange } from '../../../store/process-analysis-store/process-analysis-store.actions';
import { baseBlack, baseWhite, secondaryBase, skyDark, skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';

export const DateRangePicker = () => {
  const { t } = useTranslation();
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);

  const [value, setValue] = useState<[Date | null, Date | null]>([dayjs(startDate).toDate(), dayjs(endDate).toDate()]);

  useEffect(() => {
    setValue([dayjs(startDate).toDate(), dayjs(endDate).toDate()]);
  }, [startDate, endDate]);

  const handleChange = (dates: DatesRangeValue) => {
    const [start, end] = dates || [null, null];

    setValue([start ? dayjs(start).toDate() : null, end ? dayjs(end).toDate() : null]);

    if (start && end) {
      const startDate = dayjs(start).format('YYYY-MM-DD');
      const endDate = dayjs(end).format('YYYY-MM-DD');
      setDateRange(startDate, endDate);
    }
  };

  return (
    <StyledDatePickerInput
      type="range"
      value={value}
      onChange={handleChange}
      placeholder="Select date range"
      valueFormatter={({ date }) => {
        const isValidRange = Array.isArray(date) && date.every((d) => dayjs(d).isValid());

        if (isValidRange) {
          return t('process_analysis.date_range.format', {
            startDate: dayjs(date?.[0]).format('MM/DD/YYYY'),
            endDate: dayjs(date?.[1]).format('MM/DD/YYYY'),
          });
        }

        return '';
      }}
      rightSection={<Icon name="unfold_more" size={16} color={skyDark} />}
      rightSectionPointerEvents="none"
      clearable={false}
      allowSingleDateInRange={false}
    />
  );
};

export const StyledDatePickerInput = styled(DatePickerInput)`
  .mantine-DatePickerInput-input {
    height: 36px;
    width: 232px;
    border-radius: 32px;
    border: 1px solid ${skyLight};
    background-color: ${baseWhite};
    padding: 0 14px;
    font-family: 'Figtree', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 1em;
    color: ${baseBlack};
    cursor: pointer;
    
    &:focus {
      border-color: ${secondaryBase};
    }
    
    &::placeholder {
      color: ${skyDark};
    }
  }

  .mantine-DatePickerInput-rightSection {
    pointer-events: none;
  }

  :global(.mantine-DatePickerInput-day[data-selected]),
  :global(.mantine-DatePickerInput-day[data-first-in-range]),
  :global(.mantine-DatePickerInput-day[data-last-in-range]) {
    background-color: ${secondaryBase};
    color: ${baseWhite};
    border-color: ${secondaryBase};
  }
  
  :global(.mantine-DatePickerInput-day[data-in-range]:not([data-selected])) {
    background-color: rgba(87, 92, 229, 0.1);
  }

  :global(.mantine-DatePickerInput-day[data-in-range]:not([data-selected]):not([data-weekend])) {
    color: ${secondaryBase};
  }
  
  :global(.mantine-DatePickerInput-day:hover:not([data-selected]):not([data-in-range])) {
    background-color: ${skyLight};
  }
`;
